/**
 * Chatbot admin page.
 *
 * Route: /projects/:projectId/chatbot
 *
 * One consolidated home for every chatbot operation an admin performs on
 * their storefront: overview, conversations inbox, knowledge base, settings,
 * analytics, usage. Tabs are shells — this file owns the shell + the
 * already-simple tabs (Overview / Knowledge / Analytics / Usage); deeper
 * tabs (Conversations, Settings) grow in subsequent backlog items.
 *
 * Per-project isolation: every call is scoped by :projectId from the URL.
 * No endpoint reads or writes data outside the current project.
 *
 * Opt-in discipline: the chatbot widget only appears on the storefront
 * when the Chatbot plugin is installed *and* enabled. Config changes here
 * do not silently turn the customer-visible widget on — a separate
 * install/toggle action in Plugins does that.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useChatbotSocket, type ChatEvent } from '@/hooks/useChatbotSocket';
import { useChatRenderer } from '@/hooks/useChatRenderer';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  Bot,
  BookOpen,
  MessageSquare,
  Settings,
  BarChart3,
  Activity,
  RefreshCw,
  Plus,
  Trash2,
  Loader2,
  Sparkles,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  ThumbsDown,
  Package,
  Download,
  RotateCcw,
  MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage } from '@/lib/conflict';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-provider';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BusinessInfoTab } from './ChatbotBusinessInfo';
import { LowConfidenceList, FreshnessBanner } from './ChatbotQualityInsights';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// ── Query keys ───────────────────────────────────────────────────────────
//
// Sub-resource keys nest under qk.chatbot(projectId) so the existing broad
// invalidations (invalidateQueries({ queryKey: qk.chatbot(projectId) }))
// cascade to every chatbot query on this page. Tuple values are identical
// to the inline arrays previously used — no cache entry is orphaned.
// TODO: promote to qk if another page ever needs these.
const chatbotKeys = {
  analytics: (projectId: string) => [...qk.chatbot(projectId), 'analytics'] as const,
  usage: (projectId: string) => [...qk.chatbot(projectId), 'usage'] as const,
  knowledge: (projectId: string) => [...qk.chatbot(projectId), 'knowledge'] as const,
  knowledgeStats: (projectId: string) => [...qk.chatbot(projectId), 'knowledge-stats'] as const,
  conversations: (projectId: string, statusFilter: string) =>
    [...qk.chatbot(projectId), 'conversations', statusFilter] as const,
  conversation: (projectId: string, conversationId: string) =>
    [...qk.chatbot(projectId), 'conversation', conversationId] as const,
  trainingQueueAll: (projectId: string) => [...qk.chatbot(projectId), 'training-queue'] as const,
  trainingQueue: (projectId: string, includeReviewed: boolean) =>
    [...qk.chatbot(projectId), 'training-queue', includeReviewed] as const,
};

// Same tuple as the inline arrays other components may share for the
// installed-plugin list. TODO: promote to qk (qk.projectPlugins uses a
// different tuple, so adopting it here would orphan cross-page invalidation).
const installedPluginsKey = (projectId: string) => ['plugins', projectId, 'installed'] as const;

// ── Types ────────────────────────────────────────────────────────────────

type KnowledgeType = 'text' | 'document' | 'faq' | 'website' | 'url' | 'product';
type KnowledgeStatus = 'pending' | 'processing' | 'ready' | 'failed';

interface KnowledgeSource {
  _id: string;
  type: KnowledgeType;
  name: string;
  status: KnowledgeStatus;
  chunkCount?: number;
  createdAt: string;
  updatedAt?: string;
  error?: string;
}

interface KnowledgeStats {
  totalSources: number;
  readySources: number;
  totalChunks: number;
  byType?: Record<string, number>;
}

interface Conversation {
  id: string;
  project_id: string;
  status: 'active' | 'closed' | 'handoff';
  customer_email?: string | null;
  last_message_at: string;
  created_at: string;
  rating?: number | null;
  message_count?: number;
}

interface Analytics {
  totalConversations: number;
  totalMessages: number;
  avgMessagesPerConversation: number;
  avgRating: number;
  handoffRate: number;
  feedback?: {
    up: number;
    down: number;
    downUnreviewed: number;
    csat: number | null;
  };
  deadEndCount?: number;
  dailyConversations?: Array<{ day: string; count: number }>;
}

interface UsageData {
  plan: string;
  limits: { conversations: number; messages: number };
  current: { conversations: number; messages: number };
  allowed: boolean;
  reason?: string;
  period?: string;
  history: Array<{ period: string; conversations: number; messages: number }>;
}

// ── API helpers (scoped to chatbot routes + current projectId) ───────────

function useChatbotApi(projectId: string) {
  // All endpoints live under /chatbot/* and take projectId as a query param
  // (the backend accepts both query and route-param forms).
  return useMemo(
    () => ({
      getKnowledge: () =>
        api
          .get<{ success: boolean; data: KnowledgeSource[] }>('/chatbot/knowledge', {
            params: { projectId },
          })
          .then((r) => r.data.data),
      getKnowledgeStats: () =>
        api
          .get<{ success: boolean; data: KnowledgeStats }>('/chatbot/knowledge/stats', {
            params: { projectId },
          })
          .then((r) => r.data.data),
      addKnowledge: (payload: {
        type: KnowledgeType;
        name: string;
        content?: string;
        url?: string;
        question?: string;
        answer?: string;
      }) => api.post('/chatbot/knowledge', payload, { params: { projectId } }).then((r) => r.data),
      deleteKnowledge: (sourceId: string) =>
        api.delete(`/chatbot/knowledge/${sourceId}`, { params: { projectId } }).then((r) => r.data),
      processPending: (opts?: { retryFailed?: boolean }) =>
        api
          .post(
            '/chatbot/knowledge/process',
            {},
            {
              params: { projectId, ...(opts?.retryFailed ? { retryFailed: 'true' } : {}) },
            }
          )
          .then((r) => r.data),
      syncProducts: () =>
        api
          .post('/chatbot/knowledge/sync-products', {}, { params: { projectId } })
          .then((r) => r.data),
      getConversations: (status?: 'active' | 'closed' | 'handoff') =>
        api
          .get<{
            success: boolean;
            data: Conversation[];
            meta: { total: number };
          }>('/chatbot/conversations', { params: { projectId, status, limit: 50 } })
          .then((r) => r.data),
      getAnalytics: () =>
        api
          .get<{ success: boolean; data: Analytics }>('/chatbot/analytics', {
            params: { projectId },
          })
          .then((r) => r.data.data),
      getUsage: () =>
        api
          .get<{ success: boolean; data: UsageData }>('/chatbot/usage', {
            params: { projectId },
          })
          .then((r) => r.data.data),
    }),
    [projectId]
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

export default function ChatbotPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [activeTab, setActiveTab] = useState('overview');

  if (!projectId) return null;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600">
            <Bot className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">AI Chatbot</h1>
            <p className="text-muted-foreground text-sm">
              Train, monitor, and manage the assistant that talks to your customers. The chatbot
              widget is opt-in — install the Chatbot plugin from the{' '}
              <Link to={`/projects/${projectId}#plugins`} className="underline">
                Plugins tab
              </Link>{' '}
              to enable it on your storefront.
            </p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview">
            <Activity className="mr-2 h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="conversations">
            <MessageSquare className="mr-2 h-4 w-4" />
            Conversations
          </TabsTrigger>
          <TabsTrigger value="knowledge">
            <BookOpen className="mr-2 h-4 w-4" />
            Knowledge base
          </TabsTrigger>
          <TabsTrigger value="business-info">
            <MapPin className="mr-2 h-4 w-4" />
            Business info
          </TabsTrigger>
          <TabsTrigger value="training">
            <AlertTriangle className="mr-2 h-4 w-4" />
            Needs training
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <BarChart3 className="mr-2 h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="usage">
            <Package className="mr-2 h-4 w-4" />
            Usage
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <OverviewTab projectId={projectId} />
        </TabsContent>
        <TabsContent value="conversations" className="mt-4">
          <ConversationsTab projectId={projectId} />
        </TabsContent>
        <TabsContent value="knowledge" className="mt-4">
          <KnowledgeTab projectId={projectId} />
        </TabsContent>
        <TabsContent value="business-info" className="mt-4">
          <BusinessInfoTab projectId={projectId} />
        </TabsContent>
        <TabsContent value="training" className="mt-4">
          <TrainingQueueTab projectId={projectId} />
        </TabsContent>
        <TabsContent value="settings" className="mt-4">
          <SettingsTab projectId={projectId} />
        </TabsContent>
        <TabsContent value="analytics" className="mt-4">
          <AnalyticsTab projectId={projectId} />
        </TabsContent>
        <TabsContent value="usage" className="mt-4">
          <UsageTab projectId={projectId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Overview ─────────────────────────────────────────────────────────────

function OverviewTab({ projectId }: { projectId: string }) {
  const chatbot = useChatbotApi(projectId);

  const analytics = useQuery({
    queryKey: chatbotKeys.analytics(projectId),
    queryFn: chatbot.getAnalytics,
  });
  const usage = useQuery({
    queryKey: chatbotKeys.usage(projectId),
    queryFn: chatbot.getUsage,
  });
  const kb = useQuery({
    queryKey: chatbotKeys.knowledgeStats(projectId),
    queryFn: chatbot.getKnowledgeStats,
  });

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Conversations (all time)"
          value={analytics.data?.totalConversations ?? '—'}
          loading={analytics.isLoading}
          icon={<MessageSquare className="h-4 w-4" />}
        />
        <StatCard
          label="Messages"
          value={analytics.data?.totalMessages ?? '—'}
          loading={analytics.isLoading}
          icon={<Activity className="h-4 w-4" />}
        />
        <StatCard
          label="Handoff rate"
          value={analytics.data ? `${(analytics.data.handoffRate * 100).toFixed(1)}%` : '—'}
          loading={analytics.isLoading}
          icon={<AlertCircle className="h-4 w-4" />}
        />
        <StatCard
          label="Avg rating"
          value={analytics.data?.avgRating ? analytics.data.avgRating.toFixed(2) : '—'}
          loading={analytics.isLoading}
          icon={<Sparkles className="h-4 w-4" />}
        />

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" /> Knowledge base
            </CardTitle>
            <CardDescription>What the chatbot can answer from.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Sources</span>
              <span>{kb.data?.totalSources ?? '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Ready</span>
              <span>{kb.data?.readySources ?? '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Indexed chunks</span>
              <span>{kb.data?.totalChunks ?? '—'}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-4 w-4" /> Plan &amp; usage this period
            </CardTitle>
            <CardDescription>
              Usage resets monthly. Upgrade from the Plans tab in the plugin marketplace.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 text-sm">
            <div>
              <div className="mb-1 flex justify-between">
                <span className="text-muted-foreground">Conversations</span>
                <span>
                  {usage.data?.current.conversations ?? 0} /{' '}
                  {usage.data?.limits.conversations ?? '—'}
                </span>
              </div>
              <UsageBar
                value={usage.data?.current.conversations ?? 0}
                max={usage.data?.limits.conversations ?? 0}
              />
            </div>
            <div>
              <div className="mb-1 flex justify-between">
                <span className="text-muted-foreground">AI messages</span>
                <span>
                  {usage.data?.current.messages ?? 0} / {usage.data?.limits.messages ?? '—'}
                </span>
              </div>
              <UsageBar
                value={usage.data?.current.messages ?? 0}
                max={usage.data?.limits.messages ?? 0}
              />
            </div>
            {usage.data && !usage.data.allowed && (
              <div className="flex items-center gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                You've hit your plan cap. New AI replies are paused until the period resets or the
                plan is upgraded.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Phase D — quality observability. Turns silent misses into a queue. */}
      <LowConfidenceList projectId={projectId} />
    </div>
  );
}

function StatCard({
  label,
  value,
  loading,
  icon,
}: {
  label: string;
  value: string | number;
  loading?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-muted-foreground flex items-center gap-2 text-xs">
          {icon}
          {label}
        </div>
        <div className="mt-2 text-2xl font-semibold">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : value}
        </div>
      </CardContent>
    </Card>
  );
}

function UsageBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const color = pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-indigo-500';
  return (
    <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
      <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ── Conversations — live inbox + live conversation view ─────────────────
//
// Zero polling. The inbox list hydrates once via REST, then subscribes to the
// ProjectFeed WS and patches its state in place. Selecting a conversation
// opens a second WS to the ConversationRoom DO — messages stream in, AI
// tokens paint char-by-char, typing indicators light up from both sides.

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'agent' | 'system';
  content: string;
  timestamp: string;
  streaming?: boolean;
};

function ConversationsTab({ projectId }: { projectId: string }) {
  const chatbot = useChatbotApi(projectId);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'closed' | 'handoff'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [list, setList] = useState<Conversation[]>([]);

  // 1. Initial hydration via REST (one-shot, not polled).
  const convos = useQuery({
    queryKey: chatbotKeys.conversations(projectId, statusFilter),
    queryFn: () => chatbot.getConversations(statusFilter === 'all' ? undefined : statusFilter),
  });

  useEffect(() => {
    if (convos.data?.data) setList(convos.data.data);
  }, [convos.data]);

  // 2. Live feed: patch list on events from the ProjectFeed DO.
  const feedMode = useMemo(() => ({ kind: 'feed' as const, projectId }), [projectId]);
  const { status: feedStatus } = useChatbotSocket(feedMode, (ev: ChatEvent) => {
    if (ev.type === 'message.created' && ev.conversationId) {
      setList((cur) => {
        const idx = cur.findIndex((c) => c.id === ev.conversationId);
        const now = new Date(ev.at).toISOString();
        if (idx === -1) {
          // Unknown conversation — prepend a stub row; full row comes when
          // the user clicks in or on the next filter refresh.
          return [
            {
              id: ev.conversationId!,
              project_id: projectId,
              status: 'active',
              last_message_at: now,
              created_at: now,
              message_count: 1,
            },
            ...cur,
          ];
        }
        const next = cur.slice();
        next[idx] = { ...next[idx], last_message_at: now };
        // Bubble updated conversation to top.
        const [moved] = next.splice(idx, 1);
        return [moved, ...next];
      });
    }
    if (ev.type === 'conversation.status' && ev.conversationId) {
      const data = ev.data as { status?: Conversation['status'] };
      setList((cur) => {
        const mapped = cur.map((c) =>
          c.id === ev.conversationId && data?.status ? { ...c, status: data.status } : c
        );
        // When a conversation flips to handoff, bubble it to the top so an
        // admin watching the inbox notices immediately.
        if (data?.status === 'handoff') {
          const idx = mapped.findIndex((c) => c.id === ev.conversationId);
          if (idx > 0) {
            const [moved] = mapped.splice(idx, 1);
            return [moved, ...mapped];
          }
        }
        return mapped;
      });
    }
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
      {/* Inbox list */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base">Inbox</CardTitle>
              <CardDescription className="flex items-center gap-1.5 text-xs">
                <span
                  className={`inline-block h-2 w-2 rounded-full ${
                    feedStatus === 'open'
                      ? 'bg-green-500'
                      : feedStatus === 'connecting'
                        ? 'bg-amber-500'
                        : 'bg-red-500'
                  }`}
                />
                {feedStatus === 'open' ? 'Live' : feedStatus}
              </CardDescription>
            </div>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
            >
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="handoff">Handoff</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {convos.isLoading ? (
            <div className="text-muted-foreground flex items-center gap-2 p-4 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !list.length ? (
            <EmptyHint message="No conversations yet." />
          ) : (
            <div className="max-h-[70vh] divide-y overflow-y-auto text-sm">
              {list.map((c) => {
                const isHandoff = c.status === 'handoff';
                return (
                  <button
                    key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    className={`hover:bg-accent/50 flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
                      selectedId === c.id ? 'bg-accent' : ''
                    } ${
                      isHandoff && selectedId !== c.id
                        ? 'border-l-2 border-amber-500 bg-amber-500/5'
                        : ''
                    }`}
                  >
                    <StatusDot status={c.status} />
                    <div className="flex-1 truncate">
                      <div className="truncate font-medium">
                        {c.customer_email || 'Anonymous visitor'}
                      </div>
                      <div className="text-muted-foreground truncate text-xs">
                        {formatDistanceToNow(new Date(c.last_message_at), {
                          addSuffix: true,
                        })}
                      </div>
                    </div>
                    <Badge
                      variant={isHandoff ? 'default' : 'outline'}
                      className={`text-xs capitalize ${
                        isHandoff ? 'bg-amber-500 hover:bg-amber-600' : ''
                      }`}
                    >
                      {isHandoff ? 'Needs human' : c.status}
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversation panel */}
      {selectedId ? (
        <ConversationPanel key={selectedId} projectId={projectId} conversationId={selectedId} />
      ) : (
        <Card className="flex min-h-[500px] items-center justify-center">
          <p className="text-muted-foreground text-sm">
            Select a conversation to view the live transcript.
          </p>
        </Card>
      )}
    </div>
  );
}

async function downloadTranscript(
  projectId: string,
  conversationId: string,
  format: 'json' | 'txt'
) {
  try {
    const res = await api.get(`/chatbot/conversations/${conversationId}/export`, {
      params: { projectId, format },
      responseType: 'blob',
    });
    const blob = res.data as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${conversationId.slice(0, 12)}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast.error(apiErrorMessage(e, 'Failed to export transcript'));
  }
}

function ConversationPanel({
  projectId,
  conversationId,
}: {
  projectId: string;
  conversationId: string;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [customerTyping, setCustomerTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hydrate transcript once via REST.
  const transcript = useQuery<{
    data: { conversation: Conversation; messages: ChatMessage[] };
  }>({
    queryKey: chatbotKeys.conversation(projectId, conversationId),
    queryFn: () =>
      api
        .get(`/chatbot/conversations/${conversationId}`, { params: { projectId } })
        .then((r) => r.data),
  });

  // Pull canned responses from the chatbot plugin config. Cheap — cached by
  // react-query against the same installed-plugin key as SettingsTab.
  const installedPlugins = useQuery({
    queryKey: installedPluginsKey(projectId),
    queryFn: () =>
      api
        .get<{
          success: boolean;
          data: Array<{ pluginId: string; config?: Record<string, unknown> }>;
        }>(`/plugins/projects/${projectId}`)
        .then((r) => r.data.data),
    staleTime: 60_000,
  });
  const cannedResponses = useMemo<Array<{ id: string; title: string; body: string }>>(() => {
    const cfg = installedPlugins.data?.find((p) => p.pluginId === 'chatbot')?.config as
      | { cannedResponses?: Array<{ id: string; title: string; body: string }> }
      | undefined;
    return (cfg?.cannedResponses || []).filter((r) => r.body?.trim().length > 0);
  }, [installedPlugins.data]);

  useEffect(() => {
    if (transcript.data?.data?.messages) {
      setMessages(transcript.data.data.messages);
    }
  }, [transcript.data]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, customerTyping]);

  const mode = useMemo(
    () => ({ kind: 'conversation' as const, projectId, conversationId }),
    [projectId, conversationId]
  );

  const { status, send } = useChatbotSocket(mode, (ev: ChatEvent) => {
    switch (ev.type) {
      case 'message.created': {
        const d = ev.data as ChatMessage;
        // Skip if we already have it (e.g. agent echo of our own send).
        setMessages((cur) => (cur.some((m) => m.id === d.id) ? cur : [...cur, d]));
        break;
      }
      case 'ai.token': {
        const d = ev.data as { messageId: string; delta: string };
        setMessages((cur) => {
          const idx = cur.findIndex((m) => m.id === d.messageId);
          if (idx === -1) {
            return [
              ...cur,
              {
                id: d.messageId,
                role: 'assistant',
                content: d.delta,
                timestamp: new Date(ev.at).toISOString(),
                streaming: true,
              },
            ];
          }
          const next = cur.slice();
          next[idx] = { ...next[idx], content: next[idx].content + d.delta };
          return next;
        });
        break;
      }
      case 'ai.done': {
        const d = ev.data as { messageId: string; content: string };
        setMessages((cur) =>
          cur.map((m) =>
            m.id === d.messageId ? { ...m, content: d.content, streaming: false } : m
          )
        );
        break;
      }
      case 'typing.start': {
        const d = ev.data as { actor?: string };
        if (d?.actor === 'customer' || d?.actor === 'ai') setCustomerTyping(true);
        break;
      }
      case 'typing.stop': {
        const d = ev.data as { actor?: string };
        if (d?.actor === 'customer' || d?.actor === 'ai') setCustomerTyping(false);
        break;
      }
    }
  });

  const [sending, setSending] = useState(false);

  const sendMessage = async () => {
    const text = draft.trim();
    if (!text) return;

    // Try WebSocket first, fall back to REST if WS is not open.
    if (status === 'open') {
      send({ type: 'message.send', data: { content: text, role: 'agent' } });
      send({ type: 'typing.stop' });
      setDraft('');
    } else {
      // REST fallback — ensures admin can always reply.
      setSending(true);
      try {
        const res = await api.post(
          `/chatbot/conversations/${conversationId}/reply`,
          { content: text },
          { params: { projectId } }
        );
        if (res.data?.success && res.data?.data) {
          setMessages((cur) => [...cur, res.data.data]);
        }
        setDraft('');
      } catch (e) {
        toast.error(apiErrorMessage(e, 'Failed to send message'));
      } finally {
        setSending(false);
      }
    }
  };

  const onDraftChange = (v: string) => {
    setDraft(v);
    send({ type: 'typing.start' });
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => send({ type: 'typing.stop' }), 2_000);
  };

  return (
    <Card className="flex h-[70vh] flex-col overflow-hidden">
      <CardHeader className="border-b py-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm">
              {transcript.data?.data?.conversation?.customer_email || 'Anonymous visitor'}
            </CardTitle>
            <CardDescription className="flex items-center gap-1.5 text-xs">
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  status === 'open' ? 'bg-green-500' : 'bg-amber-500'
                }`}
              />
              {status === 'open' ? 'Live' : 'REST mode'} · {conversationId.slice(0, 8)}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {transcript.data?.data?.conversation?.status && (
              <Badge variant="outline" className="capitalize">
                {transcript.data.data.conversation.status}
              </Badge>
            )}
            <Button
              size="sm"
              variant="outline"
              title="Download transcript"
              onClick={() => downloadTranscript(projectId, conversationId, 'txt')}
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
        {transcript.isLoading && !messages.length ? (
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading transcript…
          </div>
        ) : (
          messages.map((m) => <MessageBubble key={m.id} msg={m} />)
        )}
        {customerTyping && (
          <div className="text-muted-foreground flex items-center gap-2 text-xs italic">
            <TypingDots /> typing…
          </div>
        )}
      </div>

      <div className="border-t p-3">
        {cannedResponses.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1">
            {cannedResponses.slice(0, 8).map((r) => (
              <Button
                key={r.id}
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                title={r.body}
                disabled={sending}
                onClick={() => onDraftChange(r.body)}
              >
                {r.title || 'Untitled'}
              </Button>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <Input
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Type a reply as agent… (Enter to send)"
            disabled={sending}
          />
          <Button onClick={sendMessage} disabled={!draft.trim() || sending}>
            Send
          </Button>
        </div>
      </div>
    </Card>
  );
}

function isChatImageUrl(s: string | undefined): boolean {
  if (!s) return false;
  return /^https?:\/\/[^\s]+\/(?:api\/v1\/)?public\/chat\/file\/[^\s]+\.(png|jpe?g|gif|webp|svg|bmp)(\?[^\s]*)?$/i.test(
    s.trim()
  );
}

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const mine = msg.role === 'agent' || msg.role === 'assistant';
  const isAI = msg.role === 'assistant';
  // Same renderer as the storefront widget — the admin sees the reply the
  // way the visitor did (links, call/directions chips, product cards).
  const renderer = useChatRenderer();
  const rich = useMemo(() => {
    if (!renderer || msg.role === 'user' || isChatImageUrl(msg.content)) return null;
    try {
      return renderer.render(msg.content, {
        streaming: Boolean(msg.streaming),
        quickReplies: !msg.streaming,
      });
    } catch {
      return null;
    }
  }, [renderer, msg.content, msg.role, msg.streaming]);
  if (rich) {
    return (
      <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
        <div
          className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
            mine
              ? isAI
                ? 'bg-indigo-50 text-indigo-900'
                : 'bg-primary text-primary-foreground'
              : 'bg-muted'
          }`}
        >
          <div className="text-[10px] font-medium uppercase opacity-70">{msg.role}</div>
          <div
            className="aapas-chat-bubble-text"
            // Safe by construction: the renderer builds HTML only from
            // escaped text + scheme-validated URLs (see tests/chat-renderer.test.ts).
            dangerouslySetInnerHTML={{ __html: rich.html }}
          />
          {msg.streaming && <span className="animate-pulse">▌</span>}
          {rich.quickReplies.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Suggested replies shown">
              {rich.quickReplies.map((q, i) => (
                <span
                  key={i}
                  className="rounded-full border border-indigo-200 bg-white px-2.5 py-0.5 text-[11px] text-indigo-700"
                >
                  {q}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
          mine
            ? isAI
              ? 'bg-indigo-50 text-indigo-900'
              : 'bg-primary text-primary-foreground'
            : 'bg-muted'
        }`}
      >
        <div className="text-[10px] font-medium uppercase opacity-70">{msg.role}</div>
        <div className="whitespace-pre-wrap">
          {isChatImageUrl(msg.content) ? (
            <a href={msg.content} target="_blank" rel="noopener noreferrer" className="block">
              <img
                src={msg.content}
                alt="Uploaded attachment"
                className="max-h-64 max-w-full rounded-md"
                loading="lazy"
              />
            </a>
          ) : (
            msg.content
          )}
          {msg.streaming && <span className="animate-pulse">▌</span>}
        </div>
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex gap-0.5">
      <span className="h-1 w-1 animate-bounce rounded-full bg-current" />
      <span
        className="h-1 w-1 animate-bounce rounded-full bg-current"
        style={{ animationDelay: '0.15s' }}
      />
      <span
        className="h-1 w-1 animate-bounce rounded-full bg-current"
        style={{ animationDelay: '0.3s' }}
      />
    </span>
  );
}

function StatusDot({ status }: { status: Conversation['status'] }) {
  const color =
    status === 'active' ? 'bg-green-500' : status === 'handoff' ? 'bg-amber-500' : 'bg-gray-400';
  return <span className={`inline-block h-2 w-2 rounded-full ${color}`} />;
}

// ── Knowledge Base ───────────────────────────────────────────────────────

function KnowledgeTab({ projectId }: { projectId: string }) {
  const chatbot = useChatbotApi(projectId);
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [addOpen, setAddOpen] = useState(false);

  const sources = useQuery({
    queryKey: chatbotKeys.knowledge(projectId),
    queryFn: chatbot.getKnowledge,
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => chatbot.deleteKnowledge(id),
    onSuccess: () => {
      toast.success('Source deleted');
      qc.invalidateQueries({ queryKey: qk.chatbot(projectId) });
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to delete source')),
  });

  const processM = useMutation({
    mutationFn: (opts?: { retryFailed?: boolean }) => chatbot.processPending(opts),
    onSuccess: (r: any) => {
      toast.success(r?.message || 'Processing started');
      qc.invalidateQueries({ queryKey: qk.chatbot(projectId) });
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Processing failed')),
  });

  const syncM = useMutation({
    mutationFn: () => chatbot.syncProducts(),
    onSuccess: (r: any) => {
      toast.success(r?.message || 'Products synced');
      qc.invalidateQueries({ queryKey: qk.chatbot(projectId) });
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Sync failed')),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle>Knowledge sources</CardTitle>
            <CardDescription>
              Text, FAQs, URLs, and uploaded documents the chatbot can cite when answering
              customers. Products are synced automatically when the Chatbot plugin is installed.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => syncM.mutate()}
              disabled={syncM.isPending}
            >
              {syncM.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Package className="mr-2 h-4 w-4" />
              )}
              Sync products
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => processM.mutate({})}
              disabled={processM.isPending}
            >
              {processM.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Process pending
            </Button>
            {sources.data?.some((s) => s.status === 'failed') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => processM.mutate({ retryFailed: true })}
                disabled={processM.isPending}
              >
                {processM.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="mr-2 h-4 w-4" />
                )}
                Retry failed
              </Button>
            )}
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Add source
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Phase D — freshness check. Cheap HEAD requests per URL source. */}
        <FreshnessBanner projectId={projectId} />
        {sources.isLoading ? (
          <div className="text-muted-foreground flex items-center gap-2 p-4 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !sources.data?.length ? (
          <EmptyHint message="No knowledge sources yet. Add a FAQ, paste text, or give the chatbot a URL to learn from." />
        ) : (
          <div className="divide-y text-sm">
            {sources.data.map((s) => (
              <div key={s._id} className="flex items-center gap-3 py-3">
                <KnowledgeStatusIcon status={s.status} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{s.name}</div>
                  <div className="text-muted-foreground flex items-center gap-2 truncate text-xs">
                    <Badge variant="outline" className="capitalize">
                      {s.type}
                    </Badge>
                    {s.chunkCount != null && <span>{s.chunkCount} chunks</span>}
                  </div>
                  {s.error && <p className="mt-1 text-xs leading-snug text-red-600">{s.error}</p>}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={async () => {
                    if (
                      await confirm({
                        title: 'Delete source?',
                        message: `Delete "${s.name}"?`,
                        danger: true,
                      })
                    ) {
                      deleteM.mutate(s._id);
                    }
                  }}
                  disabled={deleteM.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <AddSourceDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSubmit={async (payload) => {
          try {
            await chatbot.addKnowledge(payload);
            toast.success('Source added (will be indexed in the background)');
            setAddOpen(false);
            qc.invalidateQueries({ queryKey: qk.chatbot(projectId) });
          } catch (e) {
            toast.error(apiErrorMessage(e, 'Failed to add source'));
          }
        }}
      />
    </Card>
  );
}

function KnowledgeStatusIcon({ status }: { status: KnowledgeStatus }) {
  if (status === 'ready') return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  if (status === 'failed') return <XCircle className="h-4 w-4 text-red-600" />;
  if (status === 'processing') {
    return <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />;
  }
  return <AlertCircle className="h-4 w-4 text-amber-600" />;
}

function AddSourceDialog({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmit: (p: {
    type: KnowledgeType;
    name: string;
    content?: string;
    url?: string;
    question?: string;
    answer?: string;
  }) => void | Promise<void>;
}) {
  const [type, setType] = useState<KnowledgeType>('text');
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [url, setUrl] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setType('text');
    setName('');
    setContent('');
    setUrl('');
    setQuestion('');
    setAnswer('');
  };

  const submit = async () => {
    if (!name.trim()) return toast.error('Name is required');
    if (type === 'faq' && (!question.trim() || !answer.trim())) {
      return toast.error('Question and answer are required for FAQ');
    }
    if ((type === 'text' || type === 'document') && !content.trim()) {
      return toast.error('Content is required');
    }
    if ((type === 'website' || type === 'url') && !url.trim()) {
      return toast.error('URL is required');
    }
    setSubmitting(true);
    try {
      await onSubmit({
        type,
        name: name.trim(),
        content: content || undefined,
        url: url || undefined,
        question: question || undefined,
        answer: answer || undefined,
      });
      reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : (reset(), onOpenChange(v)))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add knowledge source</DialogTitle>
          <DialogDescription>
            The chatbot will cite this when answering customers. Processing happens in the
            background and may take a minute.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div>
            <Label>Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as KnowledgeType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Free-form text</SelectItem>
                <SelectItem value="faq">FAQ (Q &amp; A)</SelectItem>
                <SelectItem value="url">URL (one page)</SelectItem>
                <SelectItem value="website">Website (crawl)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Shipping policy"
            />
          </div>
          {(type === 'text' || type === 'document') && (
            <div>
              <Label>Content</Label>
              <Textarea
                rows={6}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Paste the text the chatbot should know about…"
              />
            </div>
          )}
          {(type === 'url' || type === 'website') && (
            <div>
              <Label>URL</Label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
            </div>
          )}
          {type === 'faq' && (
            <>
              <div>
                <Label>Question</Label>
                <Input
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="What's your return window?"
                />
              </div>
              <div>
                <Label>Answer</Label>
                <Textarea
                  rows={4}
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="We accept returns within 30 days…"
                />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add source
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Settings (shell — widget customisation lands in backlog #3) ─────────

// ── Settings (widget customisation + live preview) ───────────────────────

/**
 * Shape of the chatbot plugin config that admins edit here. Matches the
 * fields the storefront widget reads from `/public/chat/v2/config` plus
 * the persona fields the LLM uses. Kept as a flat POJO — the backend
 * PATCH endpoint merges it into `pluginConfigs.config`.
 *
 * Opt-in discipline: every customer-visible toggle in this form defaults
 * to FALSE. `enableHumanHandoff` in particular must be flipped on
 * deliberately — silent-default handoff would route shoppers to an
 * unmanned queue.
 */
interface ChatbotSettings {
  greeting: string;
  botName: string;
  brandName: string;
  botAvatar: string;
  launcherText: string;
  placeholder: string;
  primaryColor: string;
  position: 'bottom-right' | 'bottom-left';
  tone: 'professional' | 'friendly' | 'casual' | 'witty';
  language: string; // 'auto' or ISO code — 'auto' matches the customer's language
  maxResponseLength: 'short' | 'medium' | 'long';
  enableHumanHandoff: boolean;
  enableWhatsappHandoff: boolean;
  fallbackMessage: string;
  offlineMessage: string;
  customInstructions: string;
  suggestedPrompts: string[];
  businessHours: {
    enabled: boolean;
    timezone: string;
    schedule: Array<{
      day: 0 | 1 | 2 | 3 | 4 | 5 | 6;
      open: string;
      close: string;
      enabled: boolean;
    }>;
  };
  offlineBehavior: 'ai' | 'collect_email' | 'hidden';
  offlineAutoReply: string;
  leadCapture: {
    enabled: boolean;
    askOnDeadEnd: boolean;
  };
  fileUploads: {
    enabled: boolean;
    maxMB: number;
  };
  /**
   * Rich replies. Markdown + tap-to-call/directions chips always render;
   * these gate the two extensions (see chatbot-rich-responses-plan.md).
   */
  richResponses: {
    productCards: boolean;
    quickReplies: boolean;
  };
  cannedResponses: Array<{ id: string; title: string; body: string }>;
  proactiveTriggers: Array<{
    id: string;
    enabled: boolean;
    type: 'time-on-page' | 'exit-intent' | 'url-match';
    seconds?: number; // for time-on-page
    pathIncludes?: string; // for url-match
    message: string;
    openWindow: boolean; // true = pop open, false = just prepend an unread assistant msg
  }>;
}

const DAY_NAMES: Array<{ day: 0 | 1 | 2 | 3 | 4 | 5 | 6; label: string }> = [
  { day: 1, label: 'Mon' },
  { day: 2, label: 'Tue' },
  { day: 3, label: 'Wed' },
  { day: 4, label: 'Thu' },
  { day: 5, label: 'Fri' },
  { day: 6, label: 'Sat' },
  { day: 0, label: 'Sun' },
];

function defaultSchedule(): ChatbotSettings['businessHours']['schedule'] {
  // Monday–Friday 9–17, weekend off. Only used as a starting point when the
  // admin first enables the feature; they can edit freely after.
  return DAY_NAMES.map(({ day }) => ({
    day,
    open: '09:00',
    close: '17:00',
    enabled: day !== 0 && day !== 6,
  }));
}

const SETTINGS_DEFAULTS: ChatbotSettings = {
  greeting: 'Hello! How can I help you today?',
  botName: 'Assistant',
  brandName: '',
  botAvatar: '',
  launcherText: 'Chat with us',
  placeholder: 'Type your message...',
  primaryColor: '#6366f1',
  position: 'bottom-right',
  tone: 'friendly',
  language: 'auto',
  maxResponseLength: 'medium',
  enableHumanHandoff: false,
  enableWhatsappHandoff: false,
  fallbackMessage: "I'm sorry, I couldn't understand. Could you rephrase?",
  offlineMessage:
    "Thanks for reaching out! We're offline right now — leave your email and we'll reply as soon as we're back.",
  customInstructions: '',
  suggestedPrompts: [],
  businessHours: {
    enabled: false,
    timezone:
      typeof Intl !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
        : 'UTC',
    schedule: [],
  },
  offlineBehavior: 'ai',
  offlineAutoReply:
    "Thanks! We've got your message and will follow up by email within one business day.",
  leadCapture: {
    enabled: false,
    askOnDeadEnd: false,
  },
  fileUploads: {
    enabled: false,
    maxMB: 5,
  },
  richResponses: {
    productCards: true,
    quickReplies: false,
  },
  cannedResponses: [],
  proactiveTriggers: [],
};

function SettingsTab({ projectId }: { projectId: string }) {
  const qc = useQueryClient();

  // Pull the installed plugin list for this project; find the chatbot entry
  // so we can seed the form from whatever the admin last saved.
  const installed = useQuery({
    queryKey: installedPluginsKey(projectId),
    queryFn: () =>
      api
        .get<{
          success: boolean;
          data: Array<{ pluginId: string; isEnabled: boolean; config: Record<string, unknown> }>;
        }>(`/plugins/projects/${projectId}`)
        .then((r) => r.data.data),
  });

  const chatbotInstalled = installed.data?.find((p) => p.pluginId === 'chatbot');
  const persistedConfig = (chatbotInstalled?.config ?? {}) as Partial<ChatbotSettings>;

  const [form, setForm] = useState<ChatbotSettings>(SETTINGS_DEFAULTS);
  const [initialised, setInitialised] = useState(false);

  // Seed the form once the installed-plugins query resolves. We intentionally
  // only do this on first load — later refetches don't stomp the admin's
  // in-progress edits.
  useEffect(() => {
    if (!initialised && installed.data) {
      setForm({ ...SETTINGS_DEFAULTS, ...persistedConfig } as ChatbotSettings);
      setInitialised(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installed.data, initialised]);

  const save = useMutation({
    mutationFn: (next: ChatbotSettings) =>
      api.patch(`/plugins/projects/${projectId}/chatbot`, { config: next }),
    onSuccess: () => {
      toast.success('Chatbot settings saved');
      qc.invalidateQueries({ queryKey: installedPluginsKey(projectId) });
    },
    onError: (e) => {
      toast.error(apiErrorMessage(e, 'Save failed'));
    },
  });

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify({ ...SETTINGS_DEFAULTS, ...persistedConfig }),
    [form, persistedConfig]
  );

  if (installed.isLoading) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 p-4 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading settings…
      </div>
    );
  }

  if (!chatbotInstalled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Chatbot settings</CardTitle>
          <CardDescription>
            The Chatbot plugin is not installed on this project yet. Install it from the{' '}
            <Link to={`/projects/${projectId}#plugins`} className="underline">
              Plugins tab
            </Link>{' '}
            before configuring the widget.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const update = <K extends keyof ChatbotSettings>(k: K, v: ChatbotSettings[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_420px]">
      {/* ── Form ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
            <CardDescription>
              How the chat bubble and window look on your storefront.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label>Bot name</Label>
              <Input value={form.botName} onChange={(e) => update('botName', e.target.value)} />
              <p className="text-muted-foreground text-xs">
                Visitors always see that they are chatting with an AI assistant, whatever name you
                choose. The law requires it.
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Brand name (shown to the AI)</Label>
              <Input
                value={form.brandName}
                onChange={(e) => update('brandName', e.target.value)}
                placeholder={form.botName}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Launcher text / tooltip</Label>
              <Input
                value={form.launcherText}
                onChange={(e) => update('launcherText', e.target.value)}
                placeholder="Chat with us"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Textarea placeholder</Label>
              <Input
                value={form.placeholder}
                onChange={(e) => update('placeholder', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Primary colour</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.primaryColor}
                  onChange={(e) => update('primaryColor', e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border"
                  aria-label="Primary colour"
                />
                <Input
                  value={form.primaryColor}
                  onChange={(e) => update('primaryColor', e.target.value)}
                  className="font-mono text-xs"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Position</Label>
              <Select
                value={form.position}
                onValueChange={(v) => update('position', v as ChatbotSettings['position'])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bottom-right">Bottom right</SelectItem>
                  <SelectItem value="bottom-left">Bottom left</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5 md:col-span-2">
              <Label>Avatar image URL (optional)</Label>
              <Input
                value={form.botAvatar}
                onChange={(e) => update('botAvatar', e.target.value)}
                placeholder="https://…/avatar.png"
              />
              <p className="text-muted-foreground text-xs">
                Leave empty to show the default robot glyph.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Messaging</CardTitle>
            <CardDescription>What the bot says, and how.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>Greeting (first message on open)</Label>
              <Textarea
                rows={2}
                value={form.greeting}
                onChange={(e) => update('greeting', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Fallback message (when the bot can't answer)</Label>
              <Textarea
                rows={2}
                value={form.fallbackMessage}
                onChange={(e) => update('fallbackMessage', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Offline message</Label>
              <Textarea
                rows={2}
                value={form.offlineMessage}
                onChange={(e) => update('offlineMessage', e.target.value)}
              />
              <p className="text-muted-foreground text-xs">
                Shown when the bot is offline (business hours feature). Opt-in — empty disables the
                offline banner.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label>Tone</Label>
                <Select
                  value={form.tone}
                  onValueChange={(v) => update('tone', v as ChatbotSettings['tone'])}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="witty">Witty</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Reply language</Label>
                <Select value={form.language} onValueChange={(v) => update('language', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (match customer)</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                    <SelectItem value="pt">Portuguese</SelectItem>
                    <SelectItem value="it">Italian</SelectItem>
                    <SelectItem value="nl">Dutch</SelectItem>
                    <SelectItem value="hi">Hindi</SelectItem>
                    <SelectItem value="ar">Arabic</SelectItem>
                    <SelectItem value="ja">Japanese</SelectItem>
                    <SelectItem value="ko">Korean</SelectItem>
                    <SelectItem value="zh">Chinese</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground text-xs">
                  Auto lets the bot detect and reply in the customer's language.
                </p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Response length</Label>
                <Select
                  value={form.maxResponseLength}
                  onValueChange={(v) =>
                    update('maxResponseLength', v as ChatbotSettings['maxResponseLength'])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short">Short (1–2 sentences)</SelectItem>
                    <SelectItem value="medium">Medium (2–4 sentences)</SelectItem>
                    <SelectItem value="long">Long (4–6 sentences)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Custom instructions (optional)</Label>
              <Textarea
                rows={3}
                value={form.customInstructions}
                onChange={(e) => update('customInstructions', e.target.value)}
                placeholder="E.g. Always recommend the free shipping threshold. Never promise delivery dates."
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Suggested prompts</CardTitle>
            <CardDescription>
              Quick-reply chips shown below the greeting on first open. Up to 6 prompts. Leave empty
              to hide the chip row — this is opt-in.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {form.suggestedPrompts.length === 0 ? (
              <p className="text-muted-foreground text-xs">
                No suggested prompts yet. Add one below.
              </p>
            ) : (
              form.suggestedPrompts.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={p}
                    onChange={(e) => {
                      const next = [...form.suggestedPrompts];
                      next[i] = e.target.value;
                      update('suggestedPrompts', next);
                    }}
                    maxLength={80}
                    placeholder="e.g. Do you ship internationally?"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Remove prompt"
                    onClick={() =>
                      update(
                        'suggestedPrompts',
                        form.suggestedPrompts.filter((_, j) => j !== i)
                      )
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
            {form.suggestedPrompts.length < 6 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                onClick={() => update('suggestedPrompts', [...form.suggestedPrompts, ''])}
              >
                <Plus className="mr-2 h-4 w-4" /> Add prompt
              </Button>
            ) : (
              <p className="text-muted-foreground text-xs">Maximum of 6 prompts reached.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Canned responses</CardTitle>
            <CardDescription>
              Saved templates an agent can one-click into the reply box during a handoff. Shopper
              never sees this list — it's only surfaced inside the admin conversation view.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {form.cannedResponses.length === 0 ? (
              <p className="text-muted-foreground text-xs">
                No canned responses yet. Add one below.
              </p>
            ) : (
              form.cannedResponses.map((r, i) => (
                <div key={r.id} className="flex flex-col gap-2 rounded-md border p-3">
                  <div className="flex items-center gap-2">
                    <Input
                      value={r.title}
                      placeholder="Title (e.g. Refund policy)"
                      maxLength={60}
                      onChange={(e) => {
                        const next = [...form.cannedResponses];
                        next[i] = { ...r, title: e.target.value };
                        update('cannedResponses', next);
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Remove canned response"
                      onClick={() =>
                        update(
                          'cannedResponses',
                          form.cannedResponses.filter((_, j) => j !== i)
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Textarea
                    value={r.body}
                    placeholder="Message body…"
                    maxLength={1000}
                    rows={3}
                    onChange={(e) => {
                      const next = [...form.cannedResponses];
                      next[i] = { ...r, body: e.target.value };
                      update('cannedResponses', next);
                    }}
                  />
                </div>
              ))
            )}
            {form.cannedResponses.length < 20 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                onClick={() =>
                  update('cannedResponses', [
                    ...form.cannedResponses,
                    {
                      id:
                        typeof crypto !== 'undefined' && crypto.randomUUID
                          ? crypto.randomUUID()
                          : String(Date.now()),
                      title: '',
                      body: '',
                    },
                  ])
                }
              >
                <Plus className="mr-2 h-4 w-4" /> Add canned response
              </Button>
            ) : (
              <p className="text-muted-foreground text-xs">
                Maximum of 20 canned responses reached.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Proactive triggers</CardTitle>
            <CardDescription>
              Nudge visitors to open the chat based on their behaviour. Each trigger is disabled by
              default and fires at most once per visitor session — opt-in, never spammy.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {form.proactiveTriggers.length === 0 ? (
              <p className="text-muted-foreground text-xs">
                No proactive triggers yet. Add one below.
              </p>
            ) : (
              form.proactiveTriggers.map((t, i) => (
                <div key={t.id} className="flex flex-col gap-2 rounded-md border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={t.enabled}
                        onChange={(e) => {
                          const next = [...form.proactiveTriggers];
                          next[i] = { ...t, enabled: e.target.checked };
                          update('proactiveTriggers', next);
                        }}
                      />
                      Enabled
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Remove trigger"
                      onClick={() =>
                        update(
                          'proactiveTriggers',
                          form.proactiveTriggers.filter((_, j) => j !== i)
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Trigger</Label>
                      <Select
                        value={t.type}
                        onValueChange={(v) => {
                          const next = [...form.proactiveTriggers];
                          next[i] = { ...t, type: v as typeof t.type };
                          update('proactiveTriggers', next);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="time-on-page">After time on page</SelectItem>
                          <SelectItem value="exit-intent">Exit intent (desktop)</SelectItem>
                          <SelectItem value="url-match">When URL contains…</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {t.type === 'time-on-page' && (
                      <div>
                        <Label className="text-xs">After (seconds)</Label>
                        <Input
                          type="number"
                          min={5}
                          max={600}
                          value={t.seconds ?? 30}
                          onChange={(e) => {
                            const next = [...form.proactiveTriggers];
                            next[i] = { ...t, seconds: parseInt(e.target.value, 10) || 30 };
                            update('proactiveTriggers', next);
                          }}
                        />
                      </div>
                    )}
                    {t.type === 'url-match' && (
                      <div>
                        <Label className="text-xs">Path contains</Label>
                        <Input
                          placeholder="/pricing"
                          value={t.pathIncludes ?? ''}
                          onChange={(e) => {
                            const next = [...form.proactiveTriggers];
                            next[i] = { ...t, pathIncludes: e.target.value };
                            update('proactiveTriggers', next);
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-xs">Message shown</Label>
                    <Textarea
                      rows={2}
                      maxLength={300}
                      value={t.message}
                      placeholder="Need help picking a plan?"
                      onChange={(e) => {
                        const next = [...form.proactiveTriggers];
                        next[i] = { ...t, message: e.target.value };
                        update('proactiveTriggers', next);
                      }}
                    />
                  </div>

                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={t.openWindow}
                      onChange={(e) => {
                        const next = [...form.proactiveTriggers];
                        next[i] = { ...t, openWindow: e.target.checked };
                        update('proactiveTriggers', next);
                      }}
                    />
                    Pop the window open (otherwise the message waits as an unread bubble next to the
                    launcher)
                  </label>
                </div>
              ))
            )}
            {form.proactiveTriggers.length < 5 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                onClick={() =>
                  update('proactiveTriggers', [
                    ...form.proactiveTriggers,
                    {
                      id:
                        typeof crypto !== 'undefined' && crypto.randomUUID
                          ? crypto.randomUUID()
                          : String(Date.now()),
                      enabled: false,
                      type: 'time-on-page',
                      seconds: 30,
                      message: '',
                      openWindow: false,
                    },
                  ])
                }
              >
                <Plus className="mr-2 h-4 w-4" /> Add trigger
              </Button>
            ) : (
              <p className="text-muted-foreground text-xs">Maximum of 5 triggers reached.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Business hours</CardTitle>
            <CardDescription>
              When the store is outside business hours, decide what the widget does. This is opt-in
              — leave disabled to keep the AI answering 24/7.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={form.businessHours.enabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  update('businessHours', {
                    ...form.businessHours,
                    enabled,
                    schedule:
                      enabled && form.businessHours.schedule.length === 0
                        ? defaultSchedule()
                        : form.businessHours.schedule,
                  });
                }}
              />
              <div className="flex flex-col gap-0.5 text-sm">
                <span className="font-medium">Use a business-hours schedule</span>
                <span className="text-muted-foreground">
                  When off, the chatbot behaves the same any time of day.
                </span>
              </div>
            </label>

            {form.businessHours.enabled ? (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label>Timezone (IANA)</Label>
                  <Input
                    value={form.businessHours.timezone}
                    onChange={(e) =>
                      update('businessHours', {
                        ...form.businessHours,
                        timezone: e.target.value,
                      })
                    }
                    placeholder="America/New_York"
                    className="font-mono text-xs"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  {DAY_NAMES.map(({ day, label }) => {
                    const row = form.businessHours.schedule.find((s) => s.day === day) || {
                      day,
                      open: '09:00',
                      close: '17:00',
                      enabled: false,
                    };
                    const updateRow = (patch: Partial<typeof row>) => {
                      const next = form.businessHours.schedule.filter((s) => s.day !== day);
                      next.push({ ...row, ...patch });
                      next.sort((a, b) => a.day - b.day);
                      update('businessHours', {
                        ...form.businessHours,
                        schedule: next,
                      });
                    };
                    return (
                      <div key={day} className="flex items-center gap-3 rounded border px-3 py-2">
                        <label className="flex w-20 items-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={row.enabled}
                            onChange={(e) => updateRow({ enabled: e.target.checked })}
                          />
                          <span className="text-sm font-medium">{label}</span>
                        </label>
                        <Input
                          type="time"
                          value={row.open}
                          onChange={(e) => updateRow({ open: e.target.value })}
                          disabled={!row.enabled}
                          className="w-28"
                        />
                        <span className="text-muted-foreground text-xs">to</span>
                        <Input
                          type="time"
                          value={row.close}
                          onChange={(e) => updateRow({ close: e.target.value })}
                          disabled={!row.enabled}
                          className="w-28"
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label>When offline, the widget should…</Label>
                  <Select
                    value={form.offlineBehavior}
                    onValueChange={(v) =>
                      update('offlineBehavior', v as ChatbotSettings['offlineBehavior'])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ai">Keep answering with AI</SelectItem>
                      <SelectItem value="collect_email">Show an email-capture form</SelectItem>
                      <SelectItem value="hidden">Hide the widget entirely</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {form.offlineBehavior === 'collect_email' ? (
                  <div className="flex flex-col gap-1.5">
                    <Label>Auto-reply after email submission</Label>
                    <Textarea
                      rows={2}
                      value={form.offlineAutoReply}
                      onChange={(e) => update('offlineAutoReply', e.target.value)}
                    />
                    <p className="text-muted-foreground text-xs">
                      Submitted emails are upserted into your CRM as <code>chatbot</code>-sourced
                      leads with tag <code>offline-lead</code>.
                    </p>
                  </div>
                ) : null}
              </>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Behaviour</CardTitle>
            <CardDescription>Opt-in toggles that change what customers experience.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={form.enableHumanHandoff}
                onChange={(e) => update('enableHumanHandoff', e.target.checked)}
              />
              <div className="flex flex-col gap-0.5 text-sm">
                <span className="font-medium">Allow human handoff</span>
                <span className="text-muted-foreground">
                  Show a "Talk to a human" button in the widget. The conversation moves to your
                  inbox. Keep off until you're ready to staff it.
                </span>
              </div>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={form.enableWhatsappHandoff}
                onChange={(e) => update('enableWhatsappHandoff', e.target.checked)}
              />
              <div className="flex flex-col gap-0.5 text-sm">
                <span className="font-medium">Enable shifting to WhatsApp</span>
                <span className="text-muted-foreground">
                  Show a "Connect on WhatsApp" button in the widget that deep-links to your store's
                  WhatsApp number. The number is picked from your{' '}
                  <Link
                    to={`/projects/${projectId}/commerce`}
                    className="text-indigo-600 underline"
                  >
                    Commerce Settings
                  </Link>
                  . If no number is configured there, the button won't appear.
                </span>
              </div>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={form.leadCapture.enabled}
                onChange={(e) =>
                  update('leadCapture', {
                    ...form.leadCapture,
                    enabled: e.target.checked,
                  })
                }
              />
              <div className="flex flex-col gap-0.5 text-sm">
                <span className="font-medium">Capture emails shared in chat</span>
                <span className="text-muted-foreground">
                  When a shopper types an email, add them to your CRM tagged <code>chatbot</code> /{' '}
                  <code>in-chat</code>. Off by default — we never silently store contact info from
                  storefront chats.
                </span>
              </div>
            </label>
            {form.leadCapture.enabled ? (
              <label className="ml-7 flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={form.leadCapture.askOnDeadEnd}
                  onChange={(e) =>
                    update('leadCapture', {
                      ...form.leadCapture,
                      askOnDeadEnd: e.target.checked,
                    })
                  }
                />
                <div className="flex flex-col gap-0.5 text-sm">
                  <span className="font-medium">Ask for email when the bot can't help</span>
                  <span className="text-muted-foreground">
                    Nudges the assistant to request an email when it doesn't know the answer, so
                    your team can follow up.
                  </span>
                </div>
              </label>
            ) : null}

            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={form.fileUploads.enabled}
                onChange={(e) =>
                  update('fileUploads', {
                    ...form.fileUploads,
                    enabled: e.target.checked,
                  })
                }
              />
              <div className="flex flex-col gap-0.5 text-sm">
                <span className="font-medium">Allow image attachments</span>
                <span className="text-muted-foreground">
                  Adds a paperclip button so shoppers can upload screenshots (useful for support).
                  Off by default — images are stored in your R2 bucket and accrue storage cost.
                </span>
              </div>
            </label>
            {form.fileUploads.enabled ? (
              <div className="ml-7 flex items-center gap-2 text-sm">
                <Label className="text-xs">Max file size (MB)</Label>
                <Input
                  type="number"
                  min={1}
                  max={25}
                  value={form.fileUploads.maxMB}
                  onChange={(e) =>
                    update('fileUploads', {
                      ...form.fileUploads,
                      maxMB: Math.max(1, Math.min(25, parseInt(e.target.value, 10) || 5)),
                    })
                  }
                  className="w-24"
                />
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Rich replies</CardTitle>
            <CardDescription>
              Replies always render as rich text: links open in a new tab, bold and lists, and
              tap-to-call / email / WhatsApp / "Get directions" chips built from your Business Info.
              These two switches control the extras.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={form.richResponses.productCards}
                onChange={(e) =>
                  update('richResponses', {
                    ...form.richResponses,
                    productCards: e.target.checked,
                  })
                }
              />
              <div className="flex flex-col gap-0.5 text-sm">
                <span className="font-medium">Show product cards</span>
                <span className="text-muted-foreground">
                  When the assistant links products from your catalog, it also shows a row of
                  tappable cards (photo, price, sale price, stock, View) under the reply. Turn off
                  to keep plain text links only.
                </span>
              </div>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={form.richResponses.quickReplies}
                onChange={(e) =>
                  update('richResponses', {
                    ...form.richResponses,
                    quickReplies: e.target.checked,
                  })
                }
              />
              <div className="flex flex-col gap-0.5 text-sm">
                <span className="font-medium">Suggest follow-up replies</span>
                <span className="text-muted-foreground">
                  After each answer the assistant offers 2–3 tappable follow-ups ("Get directions",
                  "See more options", "Talk to a human"). Off by default: it invites visitors to
                  keep chatting, which raises message volume against your plan limits.
                </span>
              </div>
            </label>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            disabled={!dirty || save.isPending}
            onClick={() => setForm({ ...SETTINGS_DEFAULTS, ...persistedConfig } as ChatbotSettings)}
          >
            Reset
          </Button>
          <Button disabled={!dirty || save.isPending} onClick={() => save.mutate(form)}>
            {save.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              'Save changes'
            )}
          </Button>
        </div>
      </div>

      {/* ── Live preview ────────────────────────────────────────────── */}
      <div className="self-start lg:sticky lg:top-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Live preview</CardTitle>
            <CardDescription>
              What your chat widget looks like with the current unsaved edits.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <WidgetPreview settings={form} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * In-admin mock of the storefront widget. Renders the chat window open
 * permanently so the admin sees greeting + colour + avatar + bubble label
 * at a glance. Purely visual — no network calls; it never hits the real
 * chatbot. The styling mirrors the customer widget's CSS tokens (position,
 * primary colour, rounded bubble) closely enough to preview the effect of
 * any setting change.
 */
function WidgetPreview({ settings }: { settings: ChatbotSettings }) {
  const s = settings;
  const initials =
    (s.botName || 'A')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join('') || 'A';

  return (
    <div
      className="relative h-[520px] w-full overflow-hidden rounded-lg border"
      style={{
        background:
          'repeating-linear-gradient(45deg,#f8fafc,#f8fafc 10px,#f1f5f9 10px,#f1f5f9 20px)',
      }}
    >
      {/* Fake storefront backdrop */}
      <div className="absolute inset-4 rounded bg-white/70 p-4 text-xs text-slate-500">
        <div className="mb-2 font-medium">Your storefront</div>
        <div className="h-2 w-32 rounded bg-slate-200" />
        <div className="mt-2 h-2 w-48 rounded bg-slate-200" />
        <div className="mt-2 h-2 w-40 rounded bg-slate-200" />
      </div>

      {/* Chat window */}
      <div
        className={`absolute w-[300px] overflow-hidden rounded-2xl bg-white shadow-xl ${
          s.position === 'bottom-right' ? 'bottom-20 right-5' : 'bottom-20 left-5'
        }`}
        style={{ border: '1px solid #e5e7eb' }}
      >
        <div
          className="flex items-center gap-3 px-4 py-3 text-white"
          style={{ background: s.primaryColor }}
        >
          <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-white/20 text-xs font-semibold">
            {s.botAvatar ? (
              <img src={s.botAvatar} alt="" className="h-full w-full object-cover" />
            ) : (
              initials
            )}
          </div>
          <div className="flex-1 text-sm">
            <div className="font-semibold">{s.botName || 'Assistant'}</div>
            <div className="text-[11px] opacity-90">AI assistant</div>
          </div>
        </div>
        {/* Mirrors the widget's fixed AI notice (lib/ai/ai-disclosure.ts); not editable. */}
        <div className="border-b bg-slate-100 px-4 py-2 text-[11px] text-slate-700">
          {s.enableHumanHandoff || s.enableWhatsappHandoff
            ? "You're chatting with an AI assistant. You can ask for a person any time."
            : "You're chatting with an AI assistant."}
        </div>
        <div className="flex min-h-[160px] flex-col gap-2 bg-slate-50 p-3">
          <div className="max-w-[85%] self-start rounded-2xl rounded-bl-sm border bg-white px-3 py-2 text-[13px] text-slate-800">
            {s.greeting || 'Hello!'}
          </div>
          {s.suggestedPrompts.filter((p) => p.trim().length > 0).slice(0, 6).length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {s.suggestedPrompts
                .filter((p) => p.trim().length > 0)
                .slice(0, 6)
                .map((p, i) => (
                  <span
                    key={i}
                    className="rounded-full border px-2.5 py-1 text-[11px]"
                    style={{
                      background: `${s.primaryColor}14`,
                      borderColor: `${s.primaryColor}4d`,
                      color: s.primaryColor,
                    }}
                  >
                    {p}
                  </span>
                ))}
            </div>
          ) : (
            <div
              className="max-w-[85%] self-end rounded-2xl rounded-br-sm px-3 py-2 text-[13px] text-white"
              style={{ background: s.primaryColor }}
            >
              Do you ship internationally?
            </div>
          )}
        </div>
        {s.enableHumanHandoff || s.enableWhatsappHandoff ? (
          <div className="flex flex-wrap gap-2 border-t bg-white px-3 py-2">
            {s.enableHumanHandoff && (
              <span
                className="inline-block rounded-full px-3 py-1 text-[11px] font-medium"
                style={{
                  background: `${s.primaryColor}1a`,
                  color: s.primaryColor,
                }}
              >
                Talk to a human
              </span>
            )}
            {s.enableWhatsappHandoff && (
              <span
                className="inline-block rounded-full px-3 py-1 text-[11px] font-medium"
                style={{
                  background: '#dcf8c6',
                  color: '#128c7e',
                }}
              >
                Connect on WhatsApp
              </span>
            )}
          </div>
        ) : null}
        <div className="flex items-end gap-2 border-t bg-white p-2">
          <div className="flex-1 truncate rounded-full border px-3 py-2 text-[12px] text-slate-400">
            {s.placeholder || 'Type your message…'}
          </div>
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full text-white"
            style={{ background: s.primaryColor }}
            aria-hidden
          >
            ›
          </div>
        </div>
      </div>

      {/* Bubble launcher */}
      <div
        className={`absolute flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg ${
          s.position === 'bottom-right' ? 'bottom-4 right-5' : 'bottom-4 left-5'
        }`}
        style={{ background: s.primaryColor }}
        title={s.launcherText || 'Chat'}
        aria-label={s.launcherText || 'Chat'}
      >
        <MessageSquare className="h-6 w-6" />
      </div>
    </div>
  );
}

// ── Needs-training queue ─────────────────────────────────────────────────

interface TrainingRow {
  id: number;
  conversation_id: string;
  content: string;
  feedback_note: string | null;
  feedback_at: string | null;
  feedback_reviewed: number;
  user_question: string | null;
  timestamp: string;
}

function TrainingQueueTab({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [includeReviewed, setIncludeReviewed] = useState(false);

  const queue = useQuery({
    queryKey: chatbotKeys.trainingQueue(projectId, includeReviewed),
    queryFn: () =>
      api
        .get<{
          success: boolean;
          data: TrainingRow[];
        }>(`/chatbot/training-queue`, {
          params: { projectId, includeReviewed: includeReviewed ? 1 : 0 },
        })
        .then((r) => r.data.data),
  });

  const review = useMutation({
    mutationFn: (messageId: number) =>
      api
        .post(`/chatbot/training-queue/${messageId}/review`, null, {
          params: { projectId },
        })
        .then((r) => r.data),
    onSuccess: () => {
      toast.success('Marked reviewed');
      qc.invalidateQueries({ queryKey: chatbotKeys.trainingQueueAll(projectId) });
    },
    onError: () => toast.error('Could not mark reviewed'),
  });

  const rows = queue.data ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <ThumbsDown className="h-4 w-4 text-amber-600" />
                Needs training
              </CardTitle>
              <CardDescription>
                Answers your shoppers rated thumbs-down. Add an FAQ or knowledge entry to fix each
                one, then mark it reviewed to clear it.
              </CardDescription>
            </div>
            <label className="text-muted-foreground flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={includeReviewed}
                onChange={(e) => setIncludeReviewed(e.target.checked)}
              />
              Show reviewed
            </label>
          </div>
        </CardHeader>
        <CardContent>
          {queue.isLoading ? (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {includeReviewed
                ? 'No thumbs-down feedback yet.'
                : 'Nothing awaiting review. Nice work.'}
            </p>
          ) : (
            <ul className="divide-y">
              {rows.map((r) => (
                <li
                  key={r.id}
                  className={`space-y-2 py-4 ${r.feedback_reviewed ? 'opacity-60' : ''}`}
                >
                  <div className="text-muted-foreground text-xs">
                    {r.feedback_at
                      ? formatDistanceToNow(new Date(r.feedback_at), {
                          addSuffix: true,
                        })
                      : formatDistanceToNow(new Date(r.timestamp), {
                          addSuffix: true,
                        })}{' '}
                    · conversation {r.conversation_id.slice(0, 8)}
                  </div>
                  {r.user_question ? (
                    <div className="bg-muted/60 rounded-md p-2 text-sm">
                      <span className="text-muted-foreground text-xs font-medium">
                        Shopper asked:
                      </span>{' '}
                      {r.user_question}
                    </div>
                  ) : null}
                  <div className="text-sm">
                    <span className="text-muted-foreground text-xs font-medium">Bot replied:</span>{' '}
                    {r.content}
                  </div>
                  {r.feedback_note ? (
                    <div className="text-xs text-amber-700">Shopper note: {r.feedback_note}</div>
                  ) : null}
                  <div className="flex gap-2">
                    {r.feedback_reviewed ? (
                      <Badge variant="outline" className="text-xs">
                        Reviewed
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={review.isPending}
                        onClick={() => review.mutate(r.id)}
                      >
                        Mark reviewed
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Analytics ────────────────────────────────────────────────────────────

function AnalyticsTab({ projectId }: { projectId: string }) {
  const chatbot = useChatbotApi(projectId);
  const analytics = useQuery({
    queryKey: chatbotKeys.analytics(projectId),
    queryFn: chatbot.getAnalytics,
  });

  if (analytics.isLoading) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 p-4 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (analytics.isError) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-red-50 p-4 text-sm text-red-800">
        <AlertCircle className="h-4 w-4 flex-shrink-0" />
        Failed to load analytics. {apiErrorMessage(analytics.error, '')}
      </div>
    );
  }

  const a = analytics.data;
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <StatCard
        label="Total conversations"
        value={a?.totalConversations ?? 0}
        icon={<MessageSquare className="h-4 w-4" />}
      />
      <StatCard
        label="Total messages"
        value={a?.totalMessages ?? 0}
        icon={<Activity className="h-4 w-4" />}
      />
      <StatCard
        label="Msgs / conversation (avg)"
        value={a?.avgMessagesPerConversation?.toFixed(1) ?? '0.0'}
        icon={<BarChart3 className="h-4 w-4" />}
      />
      <StatCard
        label="Handoff rate"
        value={a ? `${(a.handoffRate * 100).toFixed(1)}%` : '0%'}
        icon={<AlertCircle className="h-4 w-4" />}
      />
      <StatCard
        label="Avg rating"
        value={a?.avgRating ? a.avgRating.toFixed(2) : '—'}
        icon={<Sparkles className="h-4 w-4" />}
      />
      <StatCard
        label="CSAT (thumbs)"
        value={a?.feedback?.csat == null ? '—' : `${(a.feedback.csat * 100).toFixed(0)}%`}
        icon={<Sparkles className="h-4 w-4" />}
      />
      <StatCard
        label="Thumbs up / down"
        value={`${a?.feedback?.up ?? 0} / ${a?.feedback?.down ?? 0}`}
        icon={<ThumbsDown className="h-4 w-4" />}
      />
      <StatCard
        label="Dead-end conversations"
        value={a?.deadEndCount ?? 0}
        icon={<AlertTriangle className="h-4 w-4" />}
      />
      <Card className="md:col-span-2 lg:col-span-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Conversations — last 14 days</CardTitle>
          <CardDescription className="text-xs">
            One bar per day. Hover to see the count.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DailyBars data={a?.dailyConversations ?? []} />
        </CardContent>
      </Card>
    </div>
  );
}

function DailyBars({ data }: { data: Array<{ day: string; count: number }> }) {
  if (!data.length) {
    return <p className="text-muted-foreground text-xs">No conversations in the last 14 days.</p>;
  }
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="flex h-24 items-end gap-1">
      {data.map((d) => {
        const h = Math.round((d.count / max) * 100);
        return (
          <div
            key={d.day}
            className="flex flex-1 flex-col items-center gap-1"
            title={`${d.day}: ${d.count}`}
          >
            <div
              className="bg-primary/70 w-full rounded-sm"
              style={{ height: `${h}%`, minHeight: d.count > 0 ? 2 : 0 }}
            />
            <span className="text-muted-foreground text-[10px]">{d.day.slice(5)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Usage ────────────────────────────────────────────────────────────────

function UsageTab({ projectId }: { projectId: string }) {
  const chatbot = useChatbotApi(projectId);
  const usage = useQuery({
    queryKey: chatbotKeys.usage(projectId),
    queryFn: chatbot.getUsage,
  });

  if (usage.isLoading) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 p-4 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const u = usage.data;
  if (!u) return <EmptyHint message="No usage data yet." />;

  const convoPct =
    u.limits.conversations > 0
      ? Math.round((u.current.conversations / u.limits.conversations) * 100)
      : 0;
  const msgPct =
    u.limits.messages > 0 ? Math.round((u.current.messages / u.limits.messages) * 100) : 0;
  const approaching = !u.allowed ? 'hit' : convoPct >= 80 || msgPct >= 80 ? 'warn' : null;

  return (
    <div className="flex flex-col gap-4">
      {approaching === 'hit' && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <div className="font-medium">Plan cap reached</div>
            <div className="text-xs">
              New AI replies are paused until the period resets or the plan is upgraded. Upgrade
              from the Plans tab in the plugin marketplace.
            </div>
          </div>
        </div>
      )}
      {approaching === 'warn' && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <div className="font-medium">Approaching plan cap</div>
            <div className="text-xs">
              You've used over 80% of this period. Consider upgrading to avoid service interruption.
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            Current plan: <span className="capitalize">{u.plan}</span>
          </CardTitle>
          <CardDescription>
            Usage for the current billing period
            {u.period ? ` (${u.period})` : ''}. Unlimited shown as ∞.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-sm">
          <div>
            <div className="mb-1 flex justify-between">
              <span className="text-muted-foreground">Conversations</span>
              <span>
                {u.current.conversations} /{' '}
                {u.limits.conversations > 0 ? u.limits.conversations : '∞'}
              </span>
            </div>
            <UsageBar value={u.current.conversations} max={u.limits.conversations} />
          </div>
          <div>
            <div className="mb-1 flex justify-between">
              <span className="text-muted-foreground">AI messages</span>
              <span>
                {u.current.messages} / {u.limits.messages > 0 ? u.limits.messages : '∞'}
              </span>
            </div>
            <UsageBar value={u.current.messages} max={u.limits.messages} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">History</CardTitle>
          <CardDescription>Last 6 months.</CardDescription>
        </CardHeader>
        <CardContent>
          {!u.history?.length ? (
            <EmptyHint message="No history yet." />
          ) : (
            <div className="divide-y text-sm">
              {u.history.map((h) => (
                <div key={h.period} className="flex justify-between py-2">
                  <span className="text-muted-foreground">{h.period}</span>
                  <span>
                    {h.conversations} convos · {h.messages} msgs
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Shared bits ──────────────────────────────────────────────────────────

function EmptyHint({ message }: { message: string }) {
  return (
    <div className="text-muted-foreground flex items-center justify-center p-8 text-sm">
      {message}
    </div>
  );
}
